import { useState } from "react";
import { ApiError, openFile, uploadFile, type FileRef } from "../lib/api.ts";
import type { FieldDef } from "../lib/types.ts";
import { choicesOf } from "../lib/format.ts";

/** An input for one field, by interface. Values go to the API as typed; the core normalizes them. */
export function FieldInput({
  field,
  value,
  onChange,
  id,
  collection,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  id: string;
  /** Needed by attachment fields, which send files to this collection's field. */
  collection?: string;
}) {
  const f = field;
  const text = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  switch (f.interface) {
    case "boolean":
      return (
        <select id={id} value={value === true ? "1" : value === false ? "0" : ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "1")}>
          <option value="">—</option>
          <option value="1">Sim</option>
          <option value="0">Não</option>
        </select>
      );
    case "select":
      return (
        <select id={id} value={text(value)} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Escolha</option>
          {choicesOf(f).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label ?? c.value}
            </option>
          ))}
        </select>
      );
    case "multiSelect": {
      const list = Array.isArray(value) ? (value as string[]) : [];
      return (
        <select id={id} multiple value={list} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>
          {choicesOf(f).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label ?? c.value}
            </option>
          ))}
        </select>
      );
    }
    case "longText":
      return <textarea id={id} rows={3} value={text(value)} onChange={(e) => onChange(e.target.value)} />;
    case "number":
    case "percent":
      return <input id={id} type="number" step="any" value={text(value)} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "currency":
      return <input id={id} inputMode="decimal" placeholder="0,00" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />;
    case "date":
      return <input id={id} type="date" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />;
    case "datetime":
      return (
        <input
          id={id}
          type="datetime-local"
          value={value ? new Date(String(value)).toISOString().slice(0, 16) : ""}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    case "email":
      return <input id={id} type="email" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />;
    case "attachment":
      return <AttachmentInput id={id} field={f} collection={collection ?? ""} value={value} onChange={onChange} />;
    case "relation":
      return <input id={id} placeholder="id do registro" value={text(value)} onChange={(e) => onChange(e.target.value || null)} />;
    default: {
      const ph = { cpf: "000.000.000-00", cnpj: "00.000.000/0000-00", cep: "00000-000", phone: "(00) 00000-0000" }[f.interface as string];
      return <input id={id} placeholder={ph} value={text(value)} onChange={(e) => onChange(e.target.value || null)} />;
    }
  }
}

/** Attachment field: sends each chosen file right away and keeps the list of files in the value. */
function AttachmentInput({ field, collection, value, onChange, id }: { field: FieldDef; collection: string; value: unknown; onChange: (v: unknown) => void; id: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const list = (Array.isArray(value) ? value : []) as FileRef[];
  const multiple = field.options.multiple === true;
  const accept = (field.options.accept as string[] | undefined)?.join(",");

  async function pick(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setErr(null);
    try {
      const sent: FileRef[] = [];
      for (const f of [...files].slice(0, multiple ? 20 : 1)) sent.push(await uploadFile(collection, field.name, f));
      onChange(multiple ? [...list, ...sent] : sent);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Não foi possível enviar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 4 }}>
      {list.map((f) => (
        <span key={f.id} className="row" style={{ gap: 6 }}>
          <FileChip f={f} />
          <button type="button" className="linkish" title="Tirar" onClick={() => onChange(list.filter((x) => x.id !== f.id))}>
            ×
          </button>
        </span>
      ))}
      {(multiple || !list.length) && (
        <input id={id} type="file" multiple={multiple} accept={accept} disabled={busy} onChange={(e) => (pick(e.target.files), (e.target.value = ""))} />
      )}
      {busy && <span className="muted">Enviando…</span>}
      {err && <span className="err">{err}</span>}
    </div>
  );
}

/** A file name that opens the file. */
export function FileChip({ f }: { f: FileRef }) {
  return (
    <button type="button" className="chip gray" style={{ border: 0, cursor: "pointer" }} title={`${f.mime}, ${formatSize(f.size)}`} onClick={(e) => (e.stopPropagation(), openFile(f).catch(() => alert("Não foi possível abrir o arquivo")))}>
      📎 {f.name}
    </button>
  );
}

function formatSize(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;
}
